export type GlobalAchievementMetric =
  | "wins"
  | "dailyStreak"
  | "unitsBuilt"
  | "unitsKilled"
  | "buildingsCaptured"
  | "incomeCollected"
  | "bestMedalTier";

export type GameplayAchievementMetric =
  | "noUnitsLost"
  | "noUnitsKilled"
  | "builtEveryUnitKind"
  | "noAirWithStartingAirport"
  | "builtOnlyOneUnitKind"
  | "builtOnlyBarracksUnits"
  | "builtOnlyFactoryUnits"
  | "builtOnlyAirportUnits"
  | "capturedEveryCity"
  | "capturedEveryProductionBuilding"
  | "capturedEveryBuilding"
  | "builtOnlyIndirectUnits"
  | "builtOnlyDirectUnits"
  | "noCaptures"
  | "noRepairs"
  | "maxCapacity"
  | "zeroFunds"
  | "onlyOneHpEnemyRemaining"
  | "killsInTurn"
  | "capturesInTurn"
  | "unitKills"
  | "sniperKills"
  | "unitCaptures"
  | "capturedAllMonumentsSameTurn"
  | "escortTruckNeverDamaged"
  | "escortNoEnemyUnitsRemaining"
  | "destroyedAllCannonsSameTurn"
  | "heavyTankKilledByCannon"
  | "riflemanKilledHeavyTank"
  | "rocketsKilledRockets";

type AchievementBase = {
  id: string;
  title: string;
  description: string;
};

type GlobalAchievementDefinition = AchievementBase & {
  category: "global";
  condition: {
    kind: "global";
    metric: GlobalAchievementMetric;
    threshold: number;
  };
};

type GameplayAchievementDefinition = AchievementBase & {
  category: "gameplay";
  condition: {
    kind: "gameplay";
    metric: GameplayAchievementMetric;
    threshold?: number;
  };
};

type MetaAchievementMetric = "discordLinked";

type MetaAchievementDefinition = AchievementBase & {
  category: "meta";
  condition: {
    kind: "meta";
    metric: MetaAchievementMetric;
  };
};

export type AchievementDefinition =
  | GlobalAchievementDefinition
  | GameplayAchievementDefinition
  | MetaAchievementDefinition;

function defineAchievements<const T extends readonly AchievementDefinition[]>(definitions: T): T {
  return definitions;
}

export const ACHIEVEMENTS = defineAchievements([
  {
    id: "wins-i",
    title: "Great Success I",
    description: "Win your first game.",
    category: "global",
    condition: { kind: "global", metric: "wins", threshold: 1 },
  },
  {
    id: "wins-ii",
    title: "Great Success II",
    description: "Win 10 games.",
    category: "global",
    condition: { kind: "global", metric: "wins", threshold: 10 },
  },
  {
    id: "wins-iii",
    title: "Great Success III",
    description: "Win 100 games.",
    category: "global",
    condition: { kind: "global", metric: "wins", threshold: 100 },
  },
  {
    id: "wins-iv",
    title: "Great Success IV",
    description: "Win 1,000 games.",
    category: "global",
    condition: { kind: "global", metric: "wins", threshold: 1_000 },
  },
  {
    id: "daily-streak-i",
    title: "Like Clockwork I",
    description: "Reach a 7-day streak.",
    category: "global",
    condition: { kind: "global", metric: "dailyStreak", threshold: 7 },
  },
  {
    id: "daily-streak-ii",
    title: "Like Clockwork II",
    description: "Reach a 30-day streak.",
    category: "global",
    condition: { kind: "global", metric: "dailyStreak", threshold: 30 },
  },
  {
    id: "units-built-i",
    title: "Drill Sergeant I",
    description: "Build 10 units.",
    category: "global",
    condition: { kind: "global", metric: "unitsBuilt", threshold: 10 },
  },
  {
    id: "units-built-ii",
    title: "Drill Sergeant II",
    description: "Build 100 units.",
    category: "global",
    condition: { kind: "global", metric: "unitsBuilt", threshold: 100 },
  },
  {
    id: "units-built-iii",
    title: "Drill Sergeant III",
    description: "Build 1,000 units.",
    category: "global",
    condition: { kind: "global", metric: "unitsBuilt", threshold: 1_000 },
  },
  {
    id: "units-built-iv",
    title: "Drill Sergeant IV",
    description: "Build 10,000 units.",
    category: "global",
    condition: { kind: "global", metric: "unitsBuilt", threshold: 10_000 },
  },
  {
    id: "units-killed-i",
    title: "Body Count I",
    description: "Destroy 10 enemy units.",
    category: "global",
    condition: { kind: "global", metric: "unitsKilled", threshold: 10 },
  },
  {
    id: "units-killed-ii",
    title: "Body Count II",
    description: "Destroy 100 enemy units.",
    category: "global",
    condition: { kind: "global", metric: "unitsKilled", threshold: 100 },
  },
  {
    id: "units-killed-iii",
    title: "Body Count III",
    description: "Destroy 1,000 enemy units.",
    category: "global",
    condition: { kind: "global", metric: "unitsKilled", threshold: 1_000 },
  },
  {
    id: "units-killed-iv",
    title: "Body Count IV",
    description: "Destroy 10,000 enemy units.",
    category: "global",
    condition: { kind: "global", metric: "unitsKilled", threshold: 10_000 },
  },
  {
    id: "buildings-captured-i",
    title: "Landlord I",
    description: "Capture 10 buildings.",
    category: "global",
    condition: { kind: "global", metric: "buildingsCaptured", threshold: 10 },
  },
  {
    id: "buildings-captured-ii",
    title: "Landlord II",
    description: "Capture 100 buildings.",
    category: "global",
    condition: { kind: "global", metric: "buildingsCaptured", threshold: 100 },
  },
  {
    id: "buildings-captured-iii",
    title: "Landlord III",
    description: "Capture 1,000 buildings.",
    category: "global",
    condition: { kind: "global", metric: "buildingsCaptured", threshold: 1_000 },
  },
  {
    id: "income-collected-i",
    title: "Investor I",
    description: "Collect 100,000 total income.",
    category: "global",
    condition: { kind: "global", metric: "incomeCollected", threshold: 100_000 },
  },
  {
    id: "income-collected-ii",
    title: "Investor II",
    description: "Collect 1,000,000 total income.",
    category: "global",
    condition: { kind: "global", metric: "incomeCollected", threshold: 1_000_000 },
  },
  {
    id: "income-collected-iii",
    title: "Investor III",
    description: "Collect 10,000,000 total income.",
    category: "global",
    condition: { kind: "global", metric: "incomeCollected", threshold: 10_000_000 },
  },
  {
    id: "medal-bronze",
    title: "On the Board",
    description: "Earn a Bronze medal or higher.",
    category: "global",
    condition: { kind: "global", metric: "bestMedalTier", threshold: 1 },
  },
  {
    id: "medal-silver",
    title: "Silver Lining",
    description: "Earn a Silver medal or higher.",
    category: "global",
    condition: { kind: "global", metric: "bestMedalTier", threshold: 2 },
  },
  {
    id: "medal-gold",
    title: "Going for Gold",
    description: "Earn a Gold medal.",
    category: "global",
    condition: { kind: "global", metric: "bestMedalTier", threshold: 3 },
  },
  {
    id: "win-no-units-lost",
    title: "Not a Scratch",
    description: "Win without losing a unit.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "noUnitsLost" },
  },
  {
    id: "win-no-units-killed",
    title: "Make Love, Not War",
    description: "Win without destroying an enemy unit.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "noUnitsKilled" },
  },
  {
    id: "win-no-repairs",
    title: "Look Ma, No Repairs!",
    description: "Win without repairing a unit.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "noRepairs" },
  },
  {
    id: "win-no-captures",
    title: "Full Steam Ahead",
    description: "Win without capturing any property.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "noCaptures" },
  },
  {
    id: "win-build-every-unit-kind",
    title: "Full Arsenal",
    description: "Win after building at least one of each unit kind.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "builtEveryUnitKind" },
  },
  {
    id: "win-one-unit-kind",
    title: "One-Trick Pony",
    description: "Win after building only one kind of unit.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "builtOnlyOneUnitKind" },
  },
  {
    id: "win-barracks-only",
    title: "Boot-y Hunter",
    description: "Win after building only barracks units.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "builtOnlyBarracksUnits" },
  },
  {
    id: "win-factory-only",
    title: "They See Me Rollin', They Hatin'",
    description: "Win after building only factory units.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "builtOnlyFactoryUnits" },
  },
  {
    id: "win-airport-only",
    title: "Death from Above",
    description: "Win after building only airport units.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "builtOnlyAirportUnits" },
  },
  {
    id: "win-indirect-only",
    title: "Dodge This",
    description: "Win after building only indirect units.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "builtOnlyIndirectUnits" },
  },
  {
    id: "win-direct-only",
    title: "Mad Max",
    description: "Win after building only direct units.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "builtOnlyDirectUnits" },
  },
  {
    id: "win-no-air-starting-airport",
    title: "Grounded",
    description: "Win on a map where you start with an airport without building any air units.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "noAirWithStartingAirport" },
  },
  {
    id: "win-capture-all-cities",
    title: "House-Tile Takeover",
    description: "Win after capturing every city on the map.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "capturedEveryCity" },
  },
  {
    id: "win-capture-all-production",
    title: "Seize the Means",
    description: "Win after capturing every production building on the map.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "capturedEveryProductionBuilding" },
  },
  {
    id: "win-capture-all-buildings",
    title: "All Your Base Are Belong to Us",
    description: "Win after capturing every building on the map.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "capturedEveryBuilding" },
  },
  {
    id: "win-max-capacity",
    title: "You Must Construct Additional Pylons",
    description: "Win at maximum unit capacity.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "maxCapacity" },
  },
  {
    id: "win-zero-funds",
    title: "Money Well Spent",
    description: "Win a game with 0 funds left.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "zeroFunds" },
  },
  {
    id: "win-monuments-same-turn",
    title: "Monumental Victory",
    description: "Win a monuments map by capturing every monument in the same turn.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "capturedAllMonumentsSameTurn" },
  },
  {
    id: "win-precious-cargo",
    title: "Precious Cargo",
    description: "Win on an escort map without any Supply Truck taking damage.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "escortTruckNeverDamaged" },
  },
  {
    id: "win-cant-catch-me-if-youre-dead",
    title: "Can't Catch Me If You're Dead",
    description: "Win an escort map with no enemy units remaining.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "escortNoEnemyUnitsRemaining" },
  },
  {
    id: "win-cannons-same-turn",
    title: "C-C-C-Cannon Breaker",
    description: "Win a cannons map by destroying every cannon in the same turn.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "destroyedAllCannonsSameTurn" },
  },
  {
    id: "heavy-tank-killed-by-cannon",
    title: "Shouldn't Have Parked There",
    description: "Lose a Heavy Tank to an enemy cannon.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "heavyTankKilledByCannon" },
  },
  {
    id: "end-turn-one-hp-enemy",
    title: "Close, but No Cigar",
    description: "End a turn with only a 1 HP enemy unit remaining.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "onlyOneHpEnemyRemaining" },
  },
  {
    id: "kills-in-turn-i",
    title: "Killing Spree",
    description: "Destroy 5 enemy units in the same turn.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "killsInTurn", threshold: 5 },
  },
  {
    id: "kills-in-turn-ii",
    title: "Monster Kill",
    description: "Destroy 10 enemy units in the same turn.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "killsInTurn", threshold: 10 },
  },
  {
    id: "rifleman-kill-heavy-tank",
    title: "Bring a Rifle to a Tank Fight",
    description: "Destroy a Heavy Tank with a Rifleman.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "riflemanKilledHeavyTank" },
  },
  {
    id: "rockets-kill-rockets",
    title: "It's Rocket Science",
    description: "Destroy a Rocket Launcher with a Rocket Launcher.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "rocketsKilledRockets" },
  },
  {
    id: "unit-kills-i",
    title: "One-Man Army",
    description: "Rack up 10 kills on a single unit.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "unitKills", threshold: 10 },
  },
  {
    id: "sniper-kills",
    title: "The White Death",
    description: "Rack up 15 kills on a Sniper.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "sniperKills", threshold: 15 },
  },
  {
    id: "unit-captures-i",
    title: "I'll Do It Myself",
    description: "Capture 10 buildings with the same unit.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "unitCaptures", threshold: 10 },
  },
  {
    id: "captures-in-turn-i",
    title: "How the turntables",
    description: "Capture 5 properties in the same turn.",
    category: "gameplay",
    condition: { kind: "gameplay", metric: "capturesInTurn", threshold: 5 },
  },
  {
    id: "discord-linked",
    title: "Radio Check",
    description: "Link your Discord account.",
    category: "meta",
    condition: { kind: "meta", metric: "discordLinked" },
  },
] as const);

export type AchievementId = (typeof ACHIEVEMENTS)[number]["id"];

export type AchievementView = AchievementDefinition & {
  earnedAt?: number;
};

export type EarnedAchievement = AchievementDefinition & {
  earnedAt: number;
};

const achievementIds = new Set<string>(ACHIEVEMENTS.map(({ id }) => id));

export function isAchievementId(value: string): value is AchievementId {
  return achievementIds.has(value);
}

export function getAchievement(id: AchievementId): AchievementDefinition {
  const achievement = ACHIEVEMENTS.find((candidate) => candidate.id === id);
  if (!achievement) {
    throw new Error(`Unknown achievement: ${id}`);
  }
  return achievement;
}

export function earnedAchievementsFromGrants(
  grants: readonly { achievementId: string; earnedAt: number }[],
): EarnedAchievement[] {
  return grants.flatMap(({ achievementId, earnedAt }) =>
    isAchievementId(achievementId) ? [{ ...getAchievement(achievementId), earnedAt }] : [],
  );
}

function visibleAchievementsForGrants(
  earnedIds: ReadonlySet<AchievementId>,
): Array<(typeof ACHIEVEMENTS)[number]> {
  const nextUnearnedGlobalMetric = new Set<GlobalAchievementMetric>();

  return ACHIEVEMENTS.filter((achievement) => {
    if (earnedIds.has(achievement.id)) return true;
    if (achievement.category !== "global") return true;

    const { metric } = achievement.condition;
    if (nextUnearnedGlobalMetric.has(metric)) return false;
    nextUnearnedGlobalMetric.add(metric);
    return true;
  });
}

export function achievementViewsFromGrants(
  grants: readonly { achievementId: string; earnedAt: number }[],
): AchievementView[] {
  const earnedAtById = new Map(
    grants
      .filter(
        (
          grant,
        ): grant is {
          achievementId: AchievementId;
          earnedAt: number;
        } => isAchievementId(grant.achievementId),
      )
      .map(({ achievementId, earnedAt }) => [achievementId, earnedAt] as const),
  );
  const earnedIds = new Set(earnedAtById.keys());

  return visibleAchievementsForGrants(earnedIds)
    .map((achievement) => ({
      ...achievement,
      earnedAt: earnedAtById.get(achievement.id),
    }))
    .sort((left, right) => {
      if (left.earnedAt === undefined && right.earnedAt === undefined) {
        return 0;
      }
      if (left.earnedAt === undefined) return -1;
      if (right.earnedAt === undefined) return 1;
      return right.earnedAt - left.earnedAt;
    });
}
